import React, { useState } from 'react';
import {
  DownloadCloud,
  RefreshCw,
  ExternalLink,
  ShieldAlert
} from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { CURRENT_CLIENT_VERSION, isVersionOutdated } from './versionConfig';

/**
 * Eski sürümde kalan kullanıcıların ekranını tamamen kilitleyen Zorunlu Güncelleme Ekranı
 */
export default function ForceUpdateLockModal({ versionConfig, user }) {
  const [isPurgingCache, setIsPurgingCache] = useState(false);

  // 1. Web ortamında (tarayıcıda) çalışan kullanıcılara mağaza APK zorunluluğu ASLA kilitlenmez
  // Web kullanıcıları doğrudan sunucudaki güncel kodu çalıştırmaktadır.
  const isNative = typeof window !== 'undefined' && Capacitor.isNativePlatform();
  if (!isNative) {
    return null;
  }

  // 2. Süperadmin asla kilitlenmez (sistem ayarlarını düzeltebilmesi için)
  if (user?.role === 'superadmin') {
    return null;
  }

  // 3. Zorunlu güncelleme aktif değilse gösterme
  if (!versionConfig || !versionConfig.isForceUpdateActive) {
    return null;
  }

  const isOutdated = isVersionOutdated(CURRENT_CLIENT_VERSION, versionConfig.minVersion);
  if (!isOutdated) {
    return null;
  }

  // Önbelleği temizle ve sayfayı zorla yenile
  const handlePurgeCacheAndReload = async () => {
    setIsPurgingCache(true);
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (let reg of regs) {
          await reg.unregister();
        }
      }
      localStorage.removeItem('kocum_app_version_config');
    } catch (e) {
      console.warn("Cache purge error:", e);
    }

    setTimeout(() => {
      window.location.reload(true);
    }, 500);
  };

  const handleDownload = () => {
    if (versionConfig.downloadUrl) {
      window.open(versionConfig.downloadUrl, '_blank', 'noopener,noreferrer');
    } else {
      handlePurgeCacheAndReload();
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] bg-slate-950 flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
      
      {/* Arka plan ışık efekti */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-orange-600/10 rounded-full blur-3xl pointer-events-none" />

      {/* Kilit Kartı (Kapatılamaz) */}
      <div className="relative w-full max-w-lg bg-slate-900 border-2 border-orange-500/40 rounded-3xl shadow-2xl p-6 sm:p-8 text-center space-y-6 animate-fade-in">
        
        {/* İkon */}
        <div className="w-20 h-20 rounded-3xl bg-gradient-to-tr from-orange-600 to-amber-500 text-white flex items-center justify-center mx-auto shadow-2xl shadow-orange-500/30 ring-8 ring-orange-500/10 animate-bounce-subtle">
          <DownloadCloud size={40} />
        </div>

        {/* Başlık ve Rozet */}
        <div className="space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-rose-500/20 text-rose-400 border border-rose-500/30">
            <ShieldAlert size={14} />
            <span>ZORUNLU GÜNCELLEME GEREKLİ</span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
            {versionConfig.title || 'Yeni Sürüm Yayınlandı!'}
          </h1>

          <div className="flex items-center justify-center gap-2 text-xs font-bold pt-1">
            <span className="px-2.5 py-1 rounded-lg bg-slate-800 text-slate-400 border border-slate-700">
              Sizin Sürümünüz: <b className="text-rose-400">v{CURRENT_CLIENT_VERSION}</b>
            </span>
            <span className="text-slate-500">➔</span>
            <span className="px-2.5 py-1 rounded-lg bg-orange-500/20 text-orange-400 border border-orange-500/30">
              Gereken Sürüm: <b>v{versionConfig.minVersion}</b>
            </span>
          </div>
        </div>

        {/* Mesaj Kutusu */}
        <div className="p-4 rounded-2xl bg-slate-950/70 border border-slate-800 text-sm text-slate-300 leading-relaxed text-left whitespace-pre-wrap">
          {versionConfig.message || 'Sistemin kararlı çalışması, yeni ders özellikleri ve anlık bildirimleri alabilmeniz için yeni sürüme geçmeniz zorunludur.'}
        </div>

        {/* Aksiyon Butonları */}
        <div className="space-y-3 pt-2">
          
          {/* Ana Güncelleme Butonu */}
          <button
            onClick={handleDownload}
            className="w-full py-4 px-6 rounded-2xl bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-500 hover:to-amber-400 text-white font-black text-base shadow-xl shadow-orange-500/30 transition-all flex items-center justify-center gap-2 active:scale-95"
          >
            <DownloadCloud size={20} />
            <span>{versionConfig.downloadUrl ? 'Şimdi Yeni Sürümü İndir / Güncelle' : 'Uygulamayı Şimdi Güncelle'}</span>
            <ExternalLink size={16} />
          </button>

          {/* Web/PWA İçin Önbellek Yenileme Butonu */}
          <button
            onClick={handlePurgeCacheAndReload}
            disabled={isPurgingCache}
            className="w-full py-3 px-4 rounded-xl border border-slate-800 hover:border-slate-700 bg-slate-800/60 hover:bg-slate-800 text-xs font-bold text-slate-400 hover:text-white transition-all flex items-center justify-center gap-2"
          >
            <RefreshCw size={14} className={isPurgingCache ? 'animate-spin text-orange-400' : ''} />
            <span>{isPurgingCache ? 'Önbellek Temizleniyor ve Sayfa Yenileniyor...' : 'Önbelleği Temizle & Yeniden Yükle'}</span>
          </button>

        </div>

        <p className="text-[11px] text-slate-500">
          * Güncelleme tamamlanana kadar uygulama erişimi kısıtlanmıştır.
        </p>

      </div>
    </div>
  );
}
